/**
 * FormatsTab — section "Formats" de la page Réglages.
 *
 * CRUD complet sur le catalogue `formats` (table de référence pour les
 * variantes d'œuvres en S2.5b ; multi-kind depuis S2.5e — un format peut
 * être à la fois `print` ET `digital`). Stéphanie y crée 30×40, 50×70, A3,
 * etc. en sélectionnant 1..N types.
 *
 * Le libellé (`label_fr`/`label_en`) n'est PAS saisi : il est dérivé
 * BE-side depuis (width_cm, height_cm) avec snap noble + fallback décimal.
 * La modal montre un preview live grâce à la réplique TS de l'algorithme
 * (`utils/formatLabel.ts`).
 *
 * `orientation` est dérivée DB-side (GENERATED) — non modifiable par l'UI.
 */

import { useState } from 'react'
import { Pencil, Plus, Power, PowerOff, Trash2 } from 'lucide-react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import KebabMenu from '@/components/ui/KebabMenu'
import Modal from '@/components/ui/Modal'
import { FormField } from '@/components/forms/FormField'
import { FormMultiSelect } from '@/components/forms/FormMultiSelect'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useCreateFormat, useDeleteFormat, useFormats, useUpdateFormat } from '@/hooks/useCatalog'
import { formatFormSchema, type FormatFormParsed, type FormatFormValues } from '@/schemas/catalog'
import {
  FORMAT_KIND_FR,
  FORMAT_KIND_OPTIONS,
  type FormatKind,
  type FormatResponse,
} from '@/types/catalog'
import { deriveFormatLabels } from '@/utils/formatLabel'

const KIND_OPTIONS = FORMAT_KIND_OPTIONS.map((k) => ({
  value: k,
  label: FORMAT_KIND_FR[k],
}))

const ORIENTATION_FR: Record<string, string> = {
  portrait: 'Portrait',
  landscape: 'Paysage',
  square: 'Carré',
}

export default function FormatsTab() {
  const { confirm } = useConfirm()
  const formatsQuery = useFormats()
  const createMutation = useCreateFormat()
  const updateMutation = useUpdateFormat()
  const deleteMutation = useDeleteFormat()

  const [editing, setEditing] = useState<FormatResponse | null>(null)
  const [creating, setCreating] = useState(false)

  const handleToggleActive = (row: FormatResponse) => {
    updateMutation.mutate(
      { id: row.id, payload: { active: !row.active } },
      {
        onSuccess: () => {
          toast.success(row.active ? `« ${row.label_fr} » désactivé` : `« ${row.label_fr} » activé`)
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Mise à jour impossible')
        },
      },
    )
  }

  const handleDelete = async (row: FormatResponse) => {
    const ok = await confirm({
      title: 'Supprimer ce format ?',
      message: `« ${row.label_fr} » sera retiré du catalogue. La suppression échoue si une variante l'utilise — désactive plutôt l'entrée.`,
      variant: 'danger',
      confirmText: 'Supprimer',
    })
    if (!ok) return
    deleteMutation.mutate(row.id, {
      onError: (err) => {
        toast.error(err.detail ?? 'Suppression impossible')
      },
      onSuccess: () => {
        toast.success(`Format « ${row.label_fr} » supprimé`)
      },
    })
  }

  const rows = formatsQuery.data ?? []
  const isLoading = formatsQuery.isLoading

  return (
    <section className="settings-section">
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Formats</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Dimensions des tirages et œuvres. L'orientation et le libellé sont dérivés
            automatiquement (largeur × hauteur + ratio).
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
          <Plus aria-hidden="true" size={16} />
          Ajouter un format
        </Button>
      </header>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          title="Aucun format dans ton catalogue"
          description="Commence par ajouter les dimensions standards de tes tirages — 30×40, 50×70, A3 — puis les supports et encadrements associés."
          action={
            <Button variant="primary" onClick={() => setCreating(true)}>
              Ajouter le premier format
            </Button>
          }
        />
      ) : (
        <div className="settings-table-wrapper">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Libellé</th>
                <th>Dimensions</th>
                <th>Orientation</th>
                <th>Types</th>
                <th>Ordre</th>
                <th>Statut</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} data-inactive={!row.active || undefined}>
                  <td>
                    <strong>{row.label_fr}</strong>
                    <span className="settings-table-secondary">{row.label_en}</span>
                  </td>
                  <td className="settings-table-numeric">
                    {Number(row.width_cm)} × {Number(row.height_cm)} cm
                  </td>
                  <td>{ORIENTATION_FR[row.orientation] ?? row.orientation}</td>
                  <td>
                    <span className="settings-format-kinds">
                      {row.kinds.length === 0 ? (
                        <span className="settings-table-secondary">—</span>
                      ) : (
                        row.kinds.map((k) => (
                          <Badge key={k} size="sm">
                            {FORMAT_KIND_FR[k as FormatKind] ?? k}
                          </Badge>
                        ))
                      )}
                    </span>
                  </td>
                  <td className="settings-table-numeric">{row.display_order}</td>
                  <td>
                    {row.active ? (
                      <Badge variant="success">Actif</Badge>
                    ) : (
                      <Badge variant="default">Désactivé</Badge>
                    )}
                  </td>
                  <td className="settings-table-actions">
                    <KebabMenu
                      ariaLabel={`Actions sur ${row.label_fr}`}
                      items={[
                        {
                          id: 'edit',
                          label: 'Modifier',
                          icon: Pencil,
                          onSelect: () => setEditing(row),
                        },
                        {
                          id: 'toggle',
                          label: row.active ? 'Désactiver' : 'Activer',
                          icon: row.active ? PowerOff : Power,
                          onSelect: () => handleToggleActive(row),
                        },
                        {
                          id: 'delete',
                          label: 'Supprimer',
                          icon: Trash2,
                          variant: 'danger',
                          onSelect: () => void handleDelete(row),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating ? (
        <FormatModal
          mode="create"
          onClose={() => setCreating(false)}
          onSubmit={(values) =>
            new Promise<void>((resolve, reject) => {
              const { labelFr } = deriveFormatLabels(values.width_cm, values.height_cm)
              createMutation.mutate(values, {
                onSuccess: () => {
                  toast.success(`Format « ${labelFr} » ajouté`)
                  setCreating(false)
                  resolve()
                },
                onError: (err) => {
                  toast.error(err.detail ?? 'Création impossible')
                  reject(err)
                },
              })
            })
          }
        />
      ) : null}

      {editing ? (
        <FormatModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={(values) =>
            new Promise<void>((resolve, reject) => {
              const { labelFr } = deriveFormatLabels(values.width_cm, values.height_cm)
              updateMutation.mutate(
                { id: editing.id, payload: values },
                {
                  onSuccess: () => {
                    toast.success(`Format « ${labelFr} » mis à jour`)
                    setEditing(null)
                    resolve()
                  },
                  onError: (err) => {
                    toast.error(err.detail ?? 'Mise à jour impossible')
                    reject(err)
                  },
                },
              )
            })
          }
        />
      ) : null}
    </section>
  )
}

// ─── Modal create/edit ──────────────────────────────────────────────────

interface FormatModalProps {
  mode: 'create' | 'edit'
  initial?: FormatResponse
  onClose: () => void
  onSubmit: (values: FormatFormParsed) => Promise<void>
}

function FormatModal({ mode, initial, onClose, onSubmit }: FormatModalProps) {
  const form = useForm<FormatFormValues, unknown, FormatFormParsed>({
    resolver: zodResolver(formatFormSchema),
    defaultValues: {
      width_cm: initial ? Number(initial.width_cm) : 30,
      height_cm: initial ? Number(initial.height_cm) : 40,
      kinds: initial?.kinds && initial.kinds.length > 0 ? initial.kinds : ['print'],
      display_order: initial?.display_order ?? 0,
    },
  })

  const submitting = form.formState.isSubmitting

  const handleValid = async (values: FormatFormParsed) => {
    await onSubmit(values)
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={mode === 'create' ? 'Nouveau format' : `Modifier ${initial?.label_fr ?? ''}`}
      size="form"
      kicker="Catalogue · Formats"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleValid)} noValidate>
          <Modal.Body className="settings-form-grid">
            <div className="settings-form-row">
              <FormField
                name="width_cm"
                label="Largeur (cm)"
                type="number"
                step="0.01"
                required
                focusOnMount
              />
              <FormField name="height_cm" label="Hauteur (cm)" type="number" step="0.01" required />
            </div>

            <FormatLabelPreview />

            <FormMultiSelect
              name="kinds"
              label="Types"
              options={KIND_OPTIONS}
              required
              entityNoun="type"
              helper="Un format peut être vendu en plusieurs catégories (ex: tirage papier ET fichier digital)."
            />

            <FormField
              name="display_order"
              label="Ordre d'affichage"
              type="number"
              min={0}
              helper="0 = premier dans les selects"
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" loading={submitting}>
              {mode === 'create' ? 'Ajouter' : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}

/**
 * Preview live du libellé dérivé — réagit aux changements de width_cm /
 * height_cm dans le formulaire RHF parent. Sert d'indicateur immédiat
 * pour Stéphanie : le libellé qu'elle verra dans les selects est calculé,
 * pas saisi.
 */
function FormatLabelPreview() {
  const width = useWatch<FormatFormValues, 'width_cm'>({ name: 'width_cm' })
  const height = useWatch<FormatFormValues, 'height_cm'>({ name: 'height_cm' })
  const w = Number(width)
  const h = Number(height)
  const valid = Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0
  const { labelFr, labelEn } = valid ? deriveFormatLabels(w, h) : { labelFr: '—', labelEn: '—' }

  return (
    <div className="settings-format-preview" aria-live="polite">
      <span className="ui-eyebrow">Libellé dérivé</span>
      <strong className="settings-format-preview-fr">{labelFr}</strong>
      <span className="settings-format-preview-en">{labelEn}</span>
    </div>
  )
}
